/**
 * UI adapter: bridges React tick scheduler to EnforcePoliciesUseCase (UC-10)
 * Enable via architectureFeatures.usePolicyUseCases
 */
import { useCallback } from 'react';
import type { Policy, PolicyChallenge, PolicyGameEffect } from '../../../../types/policy';
import { enforcePoliciesUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { policyEnforcementEngine } from '../../../../services/policyEnforcementEngine';
import { policyManager } from '../../../../services/policyManager';
import { PolicyStatus } from '../../../../types/policy';

export interface EnforcePoliciesResult {
  policies: Policy[];
  challenges: PolicyChallenge[];
  policyEffects: PolicyGameEffect[];
}

/** Run policy enforcement through clean architecture use case */
export function executeEnforcePolicies(): EnforcePoliciesResult {
  if (!architectureFeatures.usePolicyUseCases) {
    throw new Error('usePolicyUseCases feature flag is disabled');
  }

  return enforcePoliciesUseCase.execute();
}

/** Legacy fallback — inline policy tick logic (migration helper) */
export function executeLegacyPolicyEnforcement(): EnforcePoliciesResult {
  policyEnforcementEngine.enforcePolicies();
  const policies = policyManager.getPolicies();
  const challenges = policyManager.getChallenges();

  const policyEffects: PolicyGameEffect[] = [];
  for (const policy of policies) {
    if (policy.status === PolicyStatus.Enforced) {
      policyEffects.push(policyManager.calculateGameEffects(policy.id));
    }
  }

  return { policies, challenges, policyEffects };
}

/**
 * Hook helper for App.tsx integration.
 * Returns a stable callback that runs UC-10 when feature flag is on.
 */
export function useEnforcePoliciesRunner() {
  return useCallback((): EnforcePoliciesResult | null => {
    if (!architectureFeatures.usePolicyUseCases) {
      return null;
    }
    return executeEnforcePolicies();
  }, []);
}